/**
 * Represents a single active grid overlay on an element.
 * Multiple instances can coexist on the same element.
 */
export interface GridInstance {
  /** Unique identifier for this grid instance */
  readonly id: string;

  /** Adapter type key (e.g. "columns", "phi", "rule-of-thirds") */
  readonly type: string;

  /** Whether this grid is currently rendered */
  visible: boolean;

  /** Custom display name; falls back to adapter.label when absent */
  name?: string;

  /** Adapter-specific settings, keyed by UIFieldDescriptor.key */
  settings: Record<string, any>;
}

export type GridLayoutDevice = "desktop" | "tablet" | "phone";
export type GridLayoutMode = "L" | "M" | "S";

export interface GridLayoutPreset {
  name: string;
  minWidth: number;
}

export const DEFAULT_GRID_LAYOUT_PRESETS: GridLayoutPreset[] = [
  { name: "L", minWidth: 1024 },
  { name: "M", minWidth: 768 },
  { name: "S", minWidth: 0 },
];

/**
 * A named responsive layout containing a set of grid instances.
 * Each element can have multiple layouts which resolve automatically by width.
 */
export interface GridLayout {
  /** Unique identifier for this layout */
  readonly id: string;

  /** Display name shown in the HUD */
  name: string;

  /** Minimum viewport width required for this layout to become active */
  minWidth: number;

  /** Grid instances active in this layout */
  instances: GridInstance[];
}

/**
 * Serializable snapshot of all layouts on one element.
 * Used for localStorage persistence.
 */
export interface GridElementState {
  readonly elementId: string;
  layouts: GridLayout[];
  selectedLayoutId: string;
}

let nextId = 0;

export function createGridInstanceId(): string {
  return `grid-${Date.now()}-${nextId++}`;
}

export function createGridLayoutId(): string {
  return `layout-${Date.now()}-${nextId++}`;
}

export function createDefaultResponsiveLayouts(): GridLayout[] {
  return DEFAULT_GRID_LAYOUT_PRESETS.map((preset) => ({
    id: createGridLayoutId(),
    name: preset.name,
    minWidth: preset.minWidth,
    instances: [],
  }));
}

export function normalizeGridLayoutMinWidth(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.round(value));
}

export function getGridLayoutMode(index: number): GridLayoutMode {
  if (index <= 0) {
    return "L";
  }
  if (index === 1) {
    return "M";
  }
  return "S";
}

export function resolveGridLayoutDevice(minWidth: number): GridLayoutDevice {
  if (minWidth >= 1024) {
    return "desktop";
  }
  if (minWidth >= 768) {
    return "tablet";
  }
  return "phone";
}
